import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { Search, Car, Zap, Truck, Bike, SlidersHorizontal } from "lucide-react";
import { api } from "../../lib/api";
import { PageHeader, EmptyState, Spinner } from "../../components/dashboard/DashboardShell";
import InspectorCard from "../../components/pengguna/InspectorCard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

const TYPES = [{ id: "", label: "Semua", icon: SlidersHorizontal }, { id: "bekas", label: "Mobil Bekas", icon: Car }, { id: "listrik", label: "Mobil Listrik", icon: Zap }, { id: "truk", label: "Truk", icon: Truck }, { id: "moge", label: "Motor Moge", icon: Bike }];

export default function SearchInspectorsPage() {
  const [sp, setSp] = useSearchParams();
  const vehicleType = sp.get("jenis") || "";
  const city = sp.get("kota") || "";
  const [q, setQ] = useState("");
  const [onlineOnly, setOnlineOnly] = useState(false);
  const { data: meta } = useQuery({ queryKey: ["meta"], queryFn: () => api.get("/meta").then((r) => r.data), staleTime: Infinity });
  const { data, isLoading } = useQuery({
    queryKey: ["inspectors", vehicleType, city, q, onlineOnly],
    queryFn: () => api.get("/inspectors", { params: { vehicle_type: vehicleType || undefined, city: city || undefined, q: q || undefined, online_only: onlineOnly || undefined } }).then((r) => r.data),
  });
  const setParam = (k, v) => { const n = new URLSearchParams(sp); if (v) n.set(k, v); else n.delete(k); setSp(n); };

  return (
    <div>
      <PageHeader title="Pencarian Inspector" subtitle="Temukan inspector terverifikasi sesuai jenis kendaraan dan lokasi Anda." />
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 space-y-4">
        <div className="flex flex-wrap gap-1.5" data-testid="vehicle-type-filter">
          {TYPES.map((t) => (
            <button key={t.id} onClick={() => setParam("jenis", t.id)} data-testid={`filter-type-${t.id || "all"}`}
              className={`flex items-center gap-1.5 px-3.5 py-2 text-xs font-bold rounded-full border transition-colors ${vehicleType === t.id ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-500 border-slate-200 hover:border-blue-300"}`}>
              <t.icon size={14} /> {t.label}
            </button>
          ))}
        </div>
        <div className="grid sm:grid-cols-[1fr_220px_auto] gap-3">
          <div className="flex items-center gap-2 border border-slate-200 rounded-xl px-3 h-11">
            <Search size={16} className="text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nama inspector..." data-testid="search-input" className="flex-1 text-sm outline-none bg-transparent" />
          </div>
          <Select value={city || "all"} onValueChange={(v) => setParam("kota", v === "all" ? "" : v)}>
            <SelectTrigger className="h-11 rounded-xl" data-testid="city-select"><SelectValue placeholder="Semua kota" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua kota</SelectItem>
              {meta?.cities?.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <label className="flex items-center gap-2 text-xs font-bold text-slate-600 border border-slate-200 rounded-xl px-4 h-11 cursor-pointer">
            <input type="checkbox" checked={onlineOnly} onChange={(e) => setOnlineOnly(e.target.checked)} data-testid="online-only-checkbox" className="accent-blue-600" /> Online saja
          </label>
        </div>
      </div>

      <p className="text-xs font-semibold text-slate-400 mt-5 mb-3" data-testid="results-count">{data ? `${data.length} inspector ditemukan` : ""}</p>
      {isLoading ? <Spinner /> : !data?.length ? <EmptyState icon="search" title="Tidak ada inspector yang cocok" desc="Coba ubah filter jenis kendaraan atau kota." /> : (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="inspectors-grid">
          {data.map((i) => <InspectorCard key={i.user_id} insp={i} vehicleType={vehicleType} />)}
        </div>
      )}
    </div>
  );
}
